// src/features/itinerary/components/Segment.jsx
import React from "react";
import SegmentTime from "./columns/SegmentTime";
import SegmentPath from "./columns/SegmentPath";
import SegmentContent from "./columns/SegmentContent";
import {
  computeTimeBlocks,
  getPathIconClasses,
  getSegmentTitle,
  buildContacts,
  buildLinks,
} from "../utils/itineraryUtils";
import { parseIsoDate } from "../../../utils/dateUtils";

export default function Segment({ item, isFirst, isLast }) {
  const blocks = computeTimeBlocks(item);
  const iconInfo = getPathIconClasses(item);
  const title = getSegmentTitle(item);
  const contacts = buildContacts(item);
  const links = buildLinks(item);

  // Build the inner “details” region (you can customize per type)
  const detailsNode = renderDetails(item);

  return (
    <div id={String(item.id)} className="segment" role="listitem">
      <SegmentTime blocks={blocks} />
      <SegmentPath first={isFirst} last={isLast} iconInfo={iconInfo} />
      <SegmentContent
        item={item}
        title={title}
        contacts={contacts}
        links={links}
        detailContent={detailsNode}
      />
    </div>
  );
}

function renderDetails(item) {
  const part = item._instance || "full";

  // hotel, condo, airbnb
  if (item.type === "accommodation") {
    const parsedCheckIn = parseIsoDate(item.checkInDateTime);
    const parsedCheckOut = parseIsoDate(item.checkOutDateTime);
    if (part === "start") {
      return parsedCheckIn ? (
        <p>
          Check-in: {parsedCheckIn.yyyyMmDd} at {parsedCheckIn.shortTime}
        </p>
      ) : null;
    }
    if (part === "end") {
      return parsedCheckOut ? (
        <p>
          Check-out: {parsedCheckOut.yyyyMmDd} at {parsedCheckOut.shortTime}
        </p>
      ) : null;
    }
    // full (same-day)
    return (
      <>
        {parsedCheckIn && (
          <p>
            Check-in: {parsedCheckIn.yyyyMmDd} at {parsedCheckIn.shortTime}
          </p>
        )}
        {parsedCheckOut && (
          <p>
            Check-out: {parsedCheckOut.yyyyMmDd} at {parsedCheckOut.shortTime}
          </p>
        )}
      </>
    );
  }

  // activity / idea
  if (item.type === "activity" || item.type === "idea") {
    return (
      <>
        {item.activityName && <p>{item.activityName}</p>}
        {item?.booking?.confirmation && (
          <p>Confirmation: {item.booking.confirmation}</p>
        )}
        {item?.booking?.cancellation && (
          <p>Cancellation: {item.booking.cancellation}</p>
        )}
        {item.activityDetails && <p>{item.activityDetails}</p>}
      </>
    );
  }

  // air, train, boat, car
  if (item.type === "transportation" && item.terms === "hire") {
    //item.mode !== "layover") {
    const parsedDeparture = parseIsoDate(item.departureDateTime);
    const parsedArrival = parseIsoDate(item.arrivalDateTime);
    if (part === "start") {
      return parsedDeparture ? (
        <p>
          Depart: {parsedDeparture.yyyyMmDd} at {parsedDeparture.shortTime}{" "}
        </p>
      ) : null;
    }
    if (part === "end") {
      return parsedArrival ? (
        <p>
          Arrive: {parsedArrival.yyyyMmDd} at {parsedArrival.shortTime}{" "}
        </p>
      ) : null;
    }
    // full (same-day)
    return (
      <>
        {parsedDeparture && (
          <p>
            Depart: {parsedDeparture.yyyyMmDd} at {parsedDeparture.shortTime}{" "}
          </p>
        )}
        {parsedArrival && (
          <p>
            Arrive: {parsedArrival.yyyyMmDd} at {parsedArrival.shortTime}{" "}
          </p>
        )}
      </>
    );
  }

  // Rental Car Details
  if (
    item.type === "transportation" &&
    item.mode === "car" &&
    item.terms === "rent"
  ) {
    const parsedPickup = parseIsoDate(item.pickupDateTime);
    const parsedReturn = parseIsoDate(item.returnDateTime);
    if (part === "start") {
      return parsedPickup ? (
        <p>
          Pickup: {parsedPickup.yyyyMmDd} at {parsedPickup.shortTime}{" "}
        </p>
      ) : null;
    }
    if (part === "end") {
      return parsedReturn ? (
        <p>
          Return: {parsedReturn.yyyyMmDd} at {parsedReturn.shortTime}{" "}
        </p>
      ) : null;
    }
    return (
      <>
        {parsedPickup && (
          <p>
            Pickup: {parsedPickup.yyyyMmDd} at {parsedPickup.shortTime}{" "}
          </p>
        )}
        {parsedReturn && (
          <p>
            Return: {parsedReturn.yyyyMmDd} at {parsedReturn.shortTime}{" "}
          </p>
        )}
      </>
    );
  }

  if (item.type === "transportation" && item.mode === "layover") {
    // Title + duration cover the info; keep details empty (no trigger)
    return null;
  }

  if (item.type === "tracks") {
    const d = new Date(item.created);
    return (
      <div className="mapboxgl-popup-content" style={{ pointerEvents: "auto" }}>
        <p>
          <u>Highlights</u>
        </p>
        {item.title ? <p>{item.title}</p> : null}
        <p>{d.toUTCString()}</p>
        {item.coordinates ? (
          <div>
            <hr />
            <span>
              <b>Coordinates:</b> Lat/Long
            </span>
            <div>
              <span className="popup-indent">
                {item.coordinates.latitude}, {item.coordinates.longitude}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}

// Previous un-edited chatgpt
// function renderDetails(item) {
//   if (item.type === "transportation") {
//     return (
//       <>
//         {item.departureDate && item.departureTime && (
//           <p>
//             Depart: {item.departureDate} at {item.departureTime}
//           </p>
//         )}
//         {item.arrivalDate && item.arrivalTime && (
//           <p>
//             Arrive: {item.arrivalDate} at {item.arrivalTime}
//           </p>
//         )}
//       </>
//     );
//   }

//   if (item.type === "accommodation") {
//     return (
//       <>
//         {Array.isArray(item.services) && item.services.length ? (
//           <p>Services: {item.services.join(" • ")}</p>
//         ) : null}
//       </>
//     );
//   }

//   if (item.type === "transportation" && item.mode === "layover") {
//     // We're showing duration in the left time column and the title already says where.
//     // Keep the detail body empty (so no trigger renders).
//     return null;
//   }

//   if (item.type === "tracks") {
//     const d = new Date(item.created);
//     return (
//       <div className="mapboxgl-popup-content" style={{ pointerEvents: "auto" }}>
//         <p>
//           <u>Highlights</u>
//         </p>
//         {item.title ? <p>{item.title}</p> : null}
//         <p>{d.toUTCString()}</p>
//         {item.coordinates ? (
//           <div>
//             <hr />
//             <span>
//               <b>Coordinates:</b> Lat/Long
//             </span>
//             <div>
//               <span className="popup-indent">
//                 {item.coordinates.latitude}, {item.coordinates.longitude}
//               </span>
//             </div>
//           </div>
//         ) : null}
//       </div>
//     );
//   }

//   // activity / idea
//   if (item.type === "activity" || item.type === "idea") {
//     if (item.details) return <p>{item.details}</p>;
//     return null;
//   }

//   return null;
// }
